<template>
  <div
    class="ui-kit-v2-card"
    :title="title"
    :style="{
      '--text-color': textColor || nicheCardTextColor,
      background: getBg
    }"
  >
    <div ref="dotsContainer" class="ui-kit-v2-card__dots-container">
      <img
        v-for="(dot, index) in dots"
        :key="'dot-' + index"
        :ref="dot.refName"
        :src="dot.icon"
        alt=""
      />
    </div>
    <div v-if="!preloader" class="ui-kit-v2-card__thumb-container">
      <img v-if="icon" :src="icon" alt="" />
      <slot v-if="!icon" name="icon" />
    </div>
    <div v-else class="ui-kit-v2-card__thumb-container">
      <div class="ajax__line ajax__line-card -round-" />
    </div>
    <div class="ui-kit-v2-card__main-content">
      <template v-if="preloader">
        <div class="ui-kit-v2-card__title ajax__line ajax__line-card-text -round-" />
      </template>
      <template v-else>
        <div class="ui-kit-v2-card__title" v-html="title" />
        <div class="ui-kit-v2-card__description" v-html="description" />
      </template>
    </div>
  </div>
</template>

<script>
import getColorLightness from '@/functions/getColorLightness';
import IconDots1x2 from './style/background-elements/dots-1x2.svg';
import IconDots2x2 from './style/background-elements/dots-2x2.svg';
import IconDots3x2 from './style/background-elements/dots-3x2.svg';
import IconDots4x2 from './style/background-elements/dots-4x2.svg';

export default {
  name: 'NicheCard',
  props: {
    title: {
      type: String,
      default: ''
    },
    description: {
      type: String,
      default: ''
    },
    preloader: {
      type: Boolean,
      default: false
    },
    icon: {
      type: [String, Object],
      default: undefined
    },
    /**
     * @param {string(CSS-color|preset-[green|blue|darkBlue|purple|purple2|orangePink|pink])}
     */
    bg: {
      type: String,
      default: 'transparent'
    },
    /**
     * @param {string(CSS-color)}
     */
    textColor: {
      type: String,
      default: ''
    }
  },
  data() {
    return {
      bgColorPresets: {
        green:
          'linear-gradient(308.31deg, rgba(51, 206, 105, 0.3) 20.41%, rgba(255, 255, 255, 0.3) 83.38%), #20D75A',
        blue: `
          linear-gradient(100.88deg, #7EB8FC 25.39%, #76CEFF 98.64%),
          linear-gradient(106.76deg, #9F9CFF 16.28%, #6D61F2 81.9%)`,
        darkBlue: 'linear-gradient(106.76deg, #9F9CFF 16.28%, #6D61F2 81.9%)',
        purple: `
          linear-gradient(331.49deg, #AD73F8 16%, #F98EFB 107.59%),
          linear-gradient(313.41deg, #4DC2F3 21.16%, #88FDE1 119.44%),
          linear-gradient(101.67deg, #7EB8FC 20.17%, #76CEFF 98.63%),
          linear-gradient(106.76deg, #9F9CFF 16.28%, #6D61F2 81.9%)
        `,
        purple2:
          'linear-gradient(296.64deg, rgba(167, 114, 248, 0.3) 24.34%, rgba(255, 255, 255, 0.3) 97.72%), #AD73F8',
        orangePink:
          'linear-gradient(308.31deg, rgba(248, 124, 134, 0.3) 20.41%, rgba(255, 255, 255, 0.3) 83.38%), #F87777',
        pink: 'linear-gradient(295.43deg, rgba(255, 255, 255, 0) 38.53%, rgba(255, 255, 255, 0.3) 87.38%), #F877E3',
        gray: '#F7F7FB'
      },
      dots: {
        dots1x2: {
          icon: IconDots1x2,
          refName: 'dots1x2',
          ref: {}
        },
        dots2x2: {
          icon: IconDots2x2,
          refName: 'dots2x2',
          ref: {}
        },
        dots3x2: {
          icon: IconDots3x2,
          refName: 'dots3x2',
          ref: {}
        },
        dots4x2: {
          icon: IconDots4x2,
          refName: 'dots4x2',
          ref: {}
        }
      },
      dotsVariants: [
        {
          dots1x2: {
            top: '42px',
            right: '8px'
          },
          dots2x2: {},
          dots3x2: {},
          dots4x2: {
            top: '6px',
            left: '6px',
            opacity: '0.6'
          }
        },
        {
          dots1x2: {
            top: '64px',
            right: '32px',
            transform: 'rotate(90deg)'
          },
          dots2x2: {},
          dots3x2: {},
          dots4x2: {
            top: '42px',
            left: '6px',
            opacity: '0.6'
          }
        },
        {
          dots1x2: {
            top: '64px',
            right: '32px',
            transform: 'rotate(90deg)'
          },
          dots2x2: {},
          dots3x2: {},
          dots4x2: {
            top: '30px',
            left: '-30px',
            opacity: '0.6',
            transform: 'rotate(90deg)'
          }
        },
        {
          dots1x2: {
            top: '6px',
            right: '6px'
          },
          dots2x2: {},
          dots3x2: {},
          dots4x2: {
            bottom: '6px',
            left: '6px',
            opacity: '0.6'
          }
        },
        {
          dots1x2: {},
          dots2x2: {
            top: '16px',
            right: '16px'
          },
          dots3x2: {},
          dots4x2: {
            bottom: '6px',
            left: '6px',
            opacity: '0.6'
          }
        }
      ]
    };
  },
  computed: {
    getBg() {
      return this.bg.startsWith('preset')
        ? this.bgColorPresets[this.bg.replace('preset-', '')]
        : 'linear-gradient(295.43deg, rgba(255, 255, 255, 0) 38.53%, rgba(255, 255, 255, 0.3) 87.38%), ' +
            this.bg;
    },
    nicheCardTextColor() {
      const lightnessTreshold = 186;
      const lightness = getColorLightness(this.bg);
      if (lightness > lightnessTreshold) {
        return '#757575';
      } else {
        return '#eeeeee';
      }
    }
  },
  mounted() {
    this.renderDots();
  },
  methods: {
    renderDots() {
      const randomVariant = this.getRandomDotsVariant();
      // Задем рефы каждому элементу
      for (const key of Object.keys(this.dots)) {
        this.dots[key].ref = this.$refs[key][0];
      }
      // Применяем указанные стили (положение точек) каждому элементу
      for (const [variantKey, variantValue] of Object.entries(randomVariant)) {
        for (const [stylePropKey, stylePropValue] of Object.entries(variantValue)) {
          this.dots[variantKey].ref.style[stylePropKey] = stylePropValue;
          this.dots[variantKey].ref.style.display = 'block';
        }
      }
    },
    getRandomDotsVariant(min = 0, max = this.dotsVariants.length - 1) {
      return this.dotsVariants[Math.floor(Math.random() * (max - min + 1)) + min];
    }
  }
};
</script>

<style lang="scss" scoped>
@import 'src/style/utils';

.ui-kit-v2-card {
  $screen-sm: 600px;

  position: relative;
  display: grid;
  grid-template-columns: 140px 1fr;
  width: 100%;
  height: 100%;
  border-radius: 8px;
  font-family: 'Helvetica', 'Neue', sans-serif;
  font-style: normal;
  font-weight: 500;
  overflow: hidden;
  user-select: none;
  cursor: pointer;
  transition: all 0.5s;

  @include media($screen-sm) {
    grid-template-columns: 100px 1fr;
  }

  &:hover {
    transform: scale(1.02);
  }

  &__dots-container {
    position: absolute;
    width: 100%;
    height: 100%;
    border-radius: 8px;
    user-select: none;
    pointer-events: none;

    img {
      z-index: 1;
      position: absolute;
      display: none;
    }
  }

  &__thumb-container {
    z-index: 2;
    display: flex;
    align-items: center;
    justify-content: center;
    user-select: none;
    pointer-events: none;

    img,
    div {
      height: 75px;
      width: 75px;

      @include media($screen-sm) {
        height: 52px;
        width: 52px;
      }
    }
  }

  &__main-content {
    z-index: 2;
    display: flex;
    justify-content: center;
    flex-direction: column;
    padding-right: 50px;
  }

  &__title {
    color: var(--text-color);
    font-size: 24px;
    font-size: 18px;
    line-height: 28px;
    padding-bottom: 12px;
  }

  &__description {
    color: var(--text-color);
    font-size: 16px;
    line-height: 28px;
    font-style: normal;
    font-weight: normal;
  }
}
</style>
